import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRemoteContent } from "@/lib/ContentContext";

interface NavigationProps {
  lang: "zh" | "en";
  onLangToggle: () => void;
}

export default function Navigation({ lang, onLangToggle }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const content = useRemoteContent();
  const { nav } = content;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string, scroll: boolean) => {
    setMenuOpen(false);
    if (!scroll) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-xh-bg-panel/95 backdrop-blur-md shadow-lg shadow-cyan-500/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#banner"
          onClick={(e) => { e.preventDefault(); handleNav("#banner", true); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src={nav.logoImage}
            alt={nav.logoAlt}
            className="h-14 md:h-16 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-2">
          {nav.items.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNav(item.href, item.scroll)}
              className="relative group cursor-pointer px-5 py-2 text-base font-semibold tracking-wider whitespace-nowrap text-gray-200 hover:text-cyan-300 transition-colors duration-300"
              style={{ perspective: "400px" }}
            >
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  clipPath: "polygon(8% 0%, 92% 0%, 100% 100%, 0% 100%)",
                  background:
                    "linear-gradient(180deg, rgba(6,182,212,0.18) 0%, rgba(6,182,212,0.06) 100%)",
                  border: "1px solid rgba(6,182,212,0.35)",
                  borderRadius: "2px",
                }}
              />
              <span className="absolute bottom-0 left-[8%] right-[8%] h-[2px] bg-cyan-400/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
              <span className="relative z-10">{lang === "zh" ? item.zh : item.en}</span>
            </button>
          ))}

          <button
            onClick={onLangToggle}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-500/40 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-translate-2 text-sm" />
            {lang === "zh" ? "EN" : "中文"}
          </button>
        </div>

        {/* Mobile Hamburger */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={onLangToggle}
            className="px-2.5 py-1 rounded-full border border-cyan-500/40 text-cyan-400 text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            {lang === "zh" ? "EN" : "中文"}
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer w-8 h-8 flex items-center justify-center"
          >
            <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 bg-xh-bg-panel/98 backdrop-blur-md ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-4 border-t border-cyan-500/10">
          {nav.items.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNav(item.href, item.scroll)}
              className="text-gray-300 hover:text-cyan-400 transition-colors text-sm font-medium text-left py-2 cursor-pointer whitespace-nowrap"
            >
              {lang === "zh" ? item.zh : item.en}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}
